import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { CrossPromoCards } from "../cross-promo/CrossPromoCards.js";

/**
 * Auto-close the modal if the user takes no action. Long enough to
 * read three cards without feeling rushed; short enough not to block
 * the editor when they're ready to write the next thing.
 */
const AUTO_DISMISS_MS = 8000;

export interface SuccessModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Full-screen success overlay shown after a successful share. Epic 08
 * will replace the placeholder body with cross-promo cards.
 */
export function SuccessModal({ open, onClose }: SuccessModalProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(onClose, AUTO_DISMISS_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-success-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4"
    >
      <div className="w-full max-w-sm rounded-lg bg-[var(--tg-bg)] p-6 text-center text-[var(--tg-text)] shadow-xl">
        <div className="text-3xl" aria-hidden>
          🎉
        </div>
        <h2
          id="share-success-title"
          className="mt-2 text-lg font-semibold tracking-tight"
        >
          {t("share.success.title")}
        </h2>
        <p className="mt-1 text-sm text-[var(--tg-hint)]">
          {t("share.success.subtitle")}
        </p>
        <CrossPromoCards />
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-md bg-[var(--tg-link)] px-4 py-2 text-sm font-medium text-white"
        >
          {t("share.success.continue")}
        </button>
      </div>
    </div>
  );
}
